/**
 * The answer table (API_ACCESS.md §3.4): what a transport's answer means, read once, here. Pure.
 * The HTTP status first, then the body's `code`. A rista-be handler answers almost everything
 * with HTTP 200 and puts the outcome in the body (BACKEND.md §10), so the code decides most rows.
 * rule 14: the codes are declared here, never read from v1's constants.
 */
import type { Route } from "./route";
import type { ApiResult, RawAnswer } from "./types";

/** the forced-logout codes: v1's interceptor logs out on them; the client reports `session` */
export const SESSION_CODES: readonly string[] = ["IDLE_TIMEOUT", "UNAUTHENTICATED_USER", "REST_LICENCE_OPERATION_FAILED", "REST_STALE_CLIENT_VERSION"];

/** the backend's generic feature refusal (37 files, inventory §1) */
export const FEATURE_RESTRICTED = "REST_FEATURE_RESTRICTED";

const codeOf = (body: unknown): string | null => {
  const code = body && typeof body === "object" ? (body as { code?: unknown }).code : undefined;
  return typeof code === "string" && code ? code : null;
};

export function classify<Answer>(raw: RawAnswer, declared: Pick<Route<any, any, any>, "restrictedCodes">): ApiResult<Answer> {
  if ("failure" in raw) return { ok: false, error: { kind: raw.failure } };
  const { status, body } = raw;
  if (status === 403) return { ok: false, error: { kind: "refused", by: "server" } };
  if (status === 304) return { ok: false, error: { kind: "notModified" } };
  if (status < 200 || status >= 300) return { ok: false, error: { kind: "server", status } };
  const code = codeOf(body);
  if (!code) return { ok: true, data: body as Answer };
  if (SESSION_CODES.includes(code)) return { ok: false, error: { kind: "session", code } };
  if (code === FEATURE_RESTRICTED || declared.restrictedCodes?.includes(code)) return { ok: false, error: { kind: "restricted", code, body } };
  return { ok: false, error: { kind: "rejected", code, body } };
}
